import React from 'react';
import {
  Card, CardContent, Box, Typography, Button,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Check as CheckMark,
  TrendingDown as DowngradeIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import PlanFeatureList from './PlanFeatureList';
import { buildBasicPlanFeatures } from '../utils/planFeatures';
import { formatMoney } from '../utils/money';

/**
 * The Basic (free, pay-as-you-go) plan, as a fourth card in the plan grid.
 *
 * It was a line of text under the grid, then a named card at the bottom. Being
 * a CARD IN THE GRID is the point: pay-as-you-go is one of the four things a
 * customer can be on, and anything outside the row of plans reads as a footnote
 * to the three that are in it. Priced columns compare; a paragraph does not.
 *
 * ── WHY NOT PlanCard ────────────────────────────────────────────────────────
 * PlanCard is built around a subscription: a monthly or yearly price, an
 * upgrade/downgrade relationship, and one CTA that buys it. Basic has no price
 * to show, is never an "upgrade", and carries TWO actions. Threading a
 * variant through PlanCard would put a branch on nearly every line of it and
 * leave both cards harder to read than two files.
 *
 * ── …WHICH IS NOT A LICENCE TO LOOK DIFFERENT ───────────────────────────────
 * Being a separate file cost this card its resemblance to the three beside it.
 * It had hardcoded 24px/14px type where the others take `variant="h5"` and
 * `variant="body2"` — and this app's theme scales those DOWN to 1.125rem and
 * 0.75rem, so Basic's heading and description rendered about a third larger
 * than its neighbours'. It also had a wider corner radius, no shadow, no hover
 * lift, and its own slightly different green.
 *
 * So every shared surface here now takes its value from PlanCard rather than
 * from a number typed into this file: same variants, same radius, same border,
 * same shadow and hover, same greens and greys, same price slot. What differs
 * below differs because Basic IS different — no price, two actions — not
 * because the two files drifted.
 *
 * What they must share structurally, they do share: PlanFeatureList draws the
 * ticked rows, and planFeatures.js builds them from one list of common claims
 * — so a change to a shared feature cannot land on three cards and miss the
 * fourth.
 */

// Lifted from PlanCard so the four cards are one palette. ORANGE is the price
// colour on the paid cards and is used for the same slot here; GREEN and the
// greys are its current-plan and downgrade tones.
const ORANGE = '#FF7A59';
const GREEN = '#006A63';
const GREEN_DARK = '#004F4A';
const GREEN_TINT = 'rgba(0,106,99,0.06)';
const GREY_TEXT = '#8A8F85';
const GREY_BORDER = '#DDE0D8';

/** What the primary button says and does, from the account's actual state. */
export const BASIC_PLAN_ACTION = {
  DOWNGRADE: 'downgrade',
  CURRENT: 'current',
  DEFAULT: 'default',
  // A paid plan is ending and the account moves to Basic on a known date.
  SCHEDULED: 'scheduled',
};

/**
 * Which of the three the account is in.
 *
 * There is deliberately no cancel state: Basic is what an account falls back
 * TO, so there is never anything here to cancel.
 *
 * @param hasPaidSubscription  a live paid plan is in force
 * @param isBasicActive        the account is on Basic right now
 * @param startsOn             the date a scheduled downgrade lands on Basic
 */
// OLD: export function basicPlanAction({ hasPaidSubscription, isBasicActive } = {}) {
export function basicPlanAction({ hasPaidSubscription, isBasicActive, startsOn } = {}) {
  if (hasPaidSubscription) return BASIC_PLAN_ACTION.DOWNGRADE;
  if (isBasicActive) return BASIC_PLAN_ACTION.CURRENT;
  // The paid plan runs to its period end, then the account lands here. Not a
  // trial, so not "Default plan" — that wording is for a trial only.
  if (startsOn) return BASIC_PLAN_ACTION.SCHEDULED;
  // No subscription and not yet on Basic — a trial. Basic is where the account
  // lands when the trial ends, so it is stated as the default rather than
  // offered as a change.
  return BASIC_PLAN_ACTION.DEFAULT;
}

const BUTTON_TEXT = {
  [BASIC_PLAN_ACTION.DOWNGRADE]: 'Downgrade plan',
  // REQUESTED: "Active plan" on Basic; "Default plan" only on a trial.
  // OLD: [BASIC_PLAN_ACTION.CURRENT]: 'Current plan',
  [BASIC_PLAN_ACTION.CURRENT]: 'Active plan',
  [BASIC_PLAN_ACTION.DEFAULT]: 'Default plan',
  // SCHEDULED carries its date, so it is built in buttonText below.
};

// "Nov 21, 2026" — the same format the Cancellation scheduled banner prints,
// so the card and the banner above it name the same day the same way.
const formatStartDate = (date) => new Date(date).toLocaleDateString('en-US', {
  month: 'short', day: 'numeric', year: 'numeric',
});

const buttonText = (action, startsOn) => (action === BASIC_PLAN_ACTION.SCHEDULED
  ? `Starts ${formatStartDate(startsOn)}`
  : BUTTON_TEXT[action]);

/**
 * The pill beside the name, for the two states that are not an action.
 *
 * A state and an action are different things and now look different: the pill
 * reports, the button offers. On a paid subscription there is no pill at all —
 * Basic is not where that account is.
 */
const PILL_TEXT = {
  // OLD: [BASIC_PLAN_ACTION.CURRENT]: 'Current Plan',
  [BASIC_PLAN_ACTION.CURRENT]: 'Active Plan',
  [BASIC_PLAN_ACTION.DEFAULT]: 'Default Plan',
  [BASIC_PLAN_ACTION.SCHEDULED]: 'Scheduled',
};

// White button, coloured text + outline — PlanCard's helper, and the shared
// shape for every button on the row except an emphasised filled CTA.
const outlinedIn = (color, borderColor = color, hoverTint = 'rgba(0,0,0,0.03)') => ({
  bgcolor: '#FFFFFF',
  borderColor,
  color,
  '&:hover': { borderColor, bgcolor: hoverTint },
});

/**
 * Card chrome, identical for both layouts.
 *
 * Radius, border, shadow and hover: PlanCard's neutral (non-popular,
 * non-current) card, to the value. Basic is never popular and never outlined
 * green — it is the plan you are on by default rather than the one being sold —
 * so the neutral treatment is the whole of it.
 *
 * REQUESTED for the wide row: "match the styling with the existing cards".
 * Shared from one object rather than copied into the second layout, so the two
 * cannot drift into two slightly different cards.
 */
const CARD_SX = {
  borderRadius: 3,
  border: '1px solid',
  borderColor: '#eee',
  position: 'relative',
  overflow: 'visible',
  boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 6px 24px rgba(0,0,0,0.1)',
  },
};

const BasicPlanCard = ({
  packages = [],
  onDowngrade,
  onBuyCreditPacks,
  creditPacksReady = false,
  hasPaidSubscription = false,
  isBasicActive = false,
  // Date the account moves to Basic after a downgrade, while the paid plan
  // still runs. null when nothing is scheduled.
  startsOn = null,
  disabled = false,
  // 'month' | 'year'. Basic costs nothing either way; this follows the toggle
  // above the grid only so the four price rows read as one row. A card still
  // saying "/Month" while its neighbours say "/Year" looks like a stale card.
  interval = 'month',
  /**
   * 'vertical' — a column card, the shape it has beside the paid tiers.
   * 'horizontal' — REQUESTED: one full-width row beneath them, kept short:
   *   the name and price on the left and the buttons on the right, with the
   *   features in columns between them when the card is wide enough, or
   *   beneath both when it is not. See the note on the wide row below.
   *
   * Only the ARRANGEMENT differs. Every piece below — the name, the
   * description, the price slot, the feature rows, both buttons and all of
   * their states — is built once and placed by whichever layout is asked for,
   * because the request is explicit that nothing about the content changes.
   *
   * 'horizontal' arranges itself by the CARD's width, not the screen's: one
   * row when there is room, name and buttons above the features when there is
   * less, and stacked in the column card's order when narrow. There is no
   * honest way to put three feature columns and a button beside a price in a
   * phone's width.
   */
  layout = 'vertical',
}) => {
  const features = buildBasicPlanFeatures({
    packages,
    // The same formatter the pack picker and the checkout use. A price that
    // reads "$5" here and "$5.00" there looks like two different prices.
    formatPrice: (cents) => formatMoney(cents, 'usd'),
  });

  // ── The pack prices belong with the price, not with the features ──────────
  // They are what this plan costs. Sitting in the ticked grid they were also
  // four or five lines against everything else's one, and a grid row is as
  // tall as its tallest item — so the two rows beside "Credit pack options"
  // each carried ~200px of empty space beneath them, and the whole list was
  // pushed down past a half-empty identity column. Moving one block closes
  // both gaps: the ticks left behind are all a line or two, and the space
  // under the price is where a reader looks for what they will pay.
  //
  // Partitioned here rather than in planFeatures.js so the builder keeps
  // describing the plan in one list — BasicFeaturesDialog renders it whole.
  const packs = features.filter((f) => f.id === 'credit-packs');
  const ticks = features.filter((f) => f.id !== 'credit-packs');

  // ── The two halves of the tick list ───────────────────────────────────────
  // Headed separately, as the reference asks: how the credits work, and what
  // the plan can do. Anything the builder has not grouped falls in with the
  // capabilities rather than disappearing — a new row must never go missing
  // from the card because nobody remembered to label it.
  const usageTicks = ticks.filter((f) => f.group === 'usage');
  const capabilityTicks = ticks.filter((f) => f.group !== 'usage');

  // ── The packs, as a price list ────────────────────────────────────────────
  // One row per pack, already priced and estimated by planFeatures.js — the
  // formatting stays there so the card cannot quote a price a different way
  // from the picker it opens.
  //
  // NOT selectable, though the reference drew them as radios. A control has to
  // lead somewhere, and on this card it often cannot: a paid subscriber sees
  // no Buy button here at all (they top up from their own plan card), so the
  // selection would have nothing to act on — a choice the customer makes and
  // the card then ignores. What these rows are FOR is answering "what does
  // pay-as-you-go cost"; choosing happens in the pack dialog, which is where
  // the confirm step and the card-on-file live.
  const packOptions = (packs[0]?.bullets ?? []).map((pack, i) => ({
    ...pack,
    key: pack.id != null ? String(pack.id) : `pack-${i}`,
  }));

  // Best value = most credits per unit of money, which is what the badge
  // claims; it is not an assumption that the bigger pack is always the better
  // deal, and it moves on its own if the pricing table changes. The cheapest
  // is "Starter" unless it is also the best value, in which case one badge
  // says everything. Labels, not states — nothing here is chosen.
  const bestValueKey = packOptions.length > 1
    ? packOptions.reduce((best, p) => (
      (p.credits / Math.max(p.priceCents, 1)) > (best.credits / Math.max(best.priceCents, 1)) ? p : best
    )).key
    : null;
  const starterKey = packOptions.length > 1
    ? packOptions.reduce((cheapest, p) => (p.priceCents < cheapest.priceCents ? p : cheapest)).key
    : null;

  // OLD: const action = basicPlanAction({ hasPaidSubscription, isBasicActive });
  const action = basicPlanAction({ hasPaidSubscription, isBasicActive, startsOn });
  const isDowngrade = action === BASIC_PLAN_ACTION.DOWNGRADE;

  // Buying packs is offered where it is the thing to do: on Basic, and on a
  // trial that is about to become Basic. A paid subscriber tops up from their
  // own plan card, and offering it here would read as part of downgrading.
  // …and only when the host actually wired somewhere for it to go. A button
  // that opens nothing is worse than an absent one: it reads as broken rather
  // than as unavailable.
  const showBuyPacks = !hasPaidSubscription && Boolean(onBuyCreditPacks);

  const isHorizontal = layout === 'horizontal';

  // ── The pieces ────────────────────────────────────────────────────────────
  // Built once and placed by whichever layout is asked for. The request is
  // explicit that only the arrangement changes, so there is deliberately no
  // second copy of any of this for the wide row to drift away from.

  const identity = (
    <>
      {/* Plan name — the variant, not a pixel size. The theme scales h5 down
          to 1.125rem, so a hardcoded 24px here stood a third taller than
          "Grow", "Optimize" and "Scale" beside it. */}
      {/* Name and state on one line. The pill says where the account IS; the
          button at the foot of this column offers the only move there is. They
          never contradict each other because a paid subscriber gets no pill —
          Basic is not their current plan, it is the one below it. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
        <Typography component="h3" variant="h5" fontWeight={700}>
          Basic
        </Typography>
        {!isDowngrade && (
          <Box
            component="span"
            sx={{
              px: 1.1,
              py: 0.25,
              borderRadius: 999,
              fontSize: '0.7rem',
              fontWeight: 700,
              color: GREY_TEXT,
              bgcolor: '#F4F6F2',
              border: '1px solid',
              borderColor: GREY_BORDER,
              whiteSpace: 'nowrap',
            }}
          >
            {PILL_TEXT[action]}
          </Box>
        )}
      </Box>

      {/* Description — same variant, colour and minHeight as the other three,
          so all four descriptions occupy one band and the prices under them
          line up whether the copy runs to one line or two.

          The minHeight is a COLUMN concern: it reserves a second line so four
          side-by-side cards stay in step. In the wide row there is nothing
          beside this to stay in step with, and the reserved line would just be
          a gap above the price. */}
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 2.5, minHeight: isHorizontal ? 0 : 40 }}
      >
        Best if you only need fresh local SEO content every now and then.
      </Typography>

      {/* ── The price slot ────────────────────────────────────────────────
          Same slot and same shape as the paid cards: the figure in ORANGE at
          2.2rem, the interval muted beside it, and one small green line
          beneath — the position those cards use for "Save $X a year". The
          subscription price really is zero, and the green line says what
          that does and does not mean before the eye leaves the block.

          Previously this was two lines of 22px green, which was the largest
          type on the row and pushed every feature below it out of step with
          the neighbouring cards. */}
      <Box
        data-testid="basic-plan-price"
        sx={{
          mb: isHorizontal ? 0 : 2.5,
          // A dashed rule above the price, per the reference: it separates the
          // figure from the sentence describing the plan without drawing a
          // second hard line inside a card that already has three columns
          // divided by solid ones.
          pt: 2.5,
          borderTop: '1px dashed',
          borderColor: GREY_BORDER,
        }}
      >
        <Typography
          component="span"
          sx={{ fontSize: '2.2rem', fontWeight: 700, color: ORANGE }}
        >
          $0.00
        </Typography>
        <Typography
          component="span"
          sx={{ fontSize: '0.95rem', color: 'text.secondary', ml: 0.3 }}
        >
          {interval === 'year' ? '/Year' : '/Month'}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.9, mt: 0.9 }}>
          {/* The dot carries the "this is fine, not a warning" tone that the
              sentence alone had to do in bold green. */}
          <Box
            component="span"
            aria-hidden
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: GREEN,
              boxShadow: `0 0 0 4px ${GREEN_TINT}`,
              flexShrink: 0,
            }}
          />
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: GREEN }}>
            <Box component="span">No monthly charge.</Box>{' '}
            <Box component="span">Pay as you go.</Box>
          </Typography>
        </Box>
      </Box>
    </>
  );

  // ── The pack column ───────────────────────────────────────────────────────
  // REQUESTED: the packs are a CHOICE on the card, with one CTA under them,
  // rather than a list of prices leading to a dialog that asks again.
  //
  // What it does NOT do is buy directly. CreditPackDialog exists because an
  // irreversible payment used to be one stray tap away, with no confirm step
  // and nowhere to show which card would be charged — see its own note. So the
  // choice made here is carried INTO that dialog, which opens on the selected
  // pack: one question asked once, and the confirm step still in front of the
  // money.
  // Rendered whenever there is EITHER something to choose or a button to press.
  // The two conditions are separate on purpose: the heading and the radios
  // wait for real packs — "Credit pack options" over an empty box reads as
  // "there are none" — while the button must be on screen from the first
  // paint, disabled, because a CTA that appears a second after the page does
  // reads as a glitch.
  const packsPanel = (packOptions.length > 0 || showBuyPacks) && (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {packOptions.length > 0 && (
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 2,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
          <Box
            aria-hidden
            sx={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              bgcolor: GREEN,
              color: '#FFFFFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <CheckMark sx={{ fontSize: 15 }} />
          </Box>
          <Typography component="h4" sx={{ fontSize: '0.95rem', fontWeight: 700 }}>
            {packs[0]?.label ?? 'Credit pack options'}
          </Typography>
        </Box>
        <Box
          component="span"
          sx={{
            fontSize: '0.65rem',
            fontWeight: 700,
            letterSpacing: '0.06em',
            color: GREY_TEXT,
            bgcolor: '#F0F2EE',
            px: 0.9,
            py: 0.3,
            borderRadius: 1,
            whiteSpace: 'nowrap',
          }}
        >
          TOP-UP
        </Box>
      </Box>
      )}

      {/* A list, not a form. No radio, no hover, no selected state — nothing
          here responds to a click because nothing here does anything. */}
      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 1.25 }}>
        {packOptions.map((pack) => {
          const badge = pack.key === bestValueKey
            ? { text: 'Best Value', color: '#B45309', bg: '#FFFBEB', border: '#FDE68A' }
            : (pack.key === starterKey
              ? { text: 'Starter', color: GREEN, bg: GREEN_TINT, border: 'rgba(0,106,99,0.25)' }
              : null);

          return (
            <Box
              component="li"
              key={pack.key}
              sx={{
                p: 1.75,
                borderRadius: 2.5,
                bgcolor: '#FFFFFF',
                border: '1px solid',
                borderColor: GREY_BORDER,
              }}
            >
              <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
              }}>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>
                  {pack.text}
                </Typography>
                {badge && (
                  <Box
                    component="span"
                    sx={{
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      color: badge.color,
                      bgcolor: badge.bg,
                      border: '1px solid',
                      borderColor: badge.border,
                      px: 0.9,
                      py: 0.2,
                      borderRadius: 1,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {badge.text}
                  </Box>
                )}
              </Box>
              {pack.detail && (
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    fontStyle: 'italic',
                    color: 'text.secondary',
                    mt: 0.4,
                  }}
                >
                  {pack.detail}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>

      {/* mt:auto — the CTA sits at the foot of the column however tall the
          packs are, so it lines up with the Downgrade button beside it. */}
      {showBuyPacks && (
        <Box sx={{ mt: 'auto', pt: 2.5 }}>
          <Button
            fullWidth
            size="large"
            variant="contained"
            disabled={!creditPacksReady || disabled}
            onClick={() => onBuyCreditPacks?.()}
            endIcon={<ArrowIcon />}
            sx={{
              py: 1.4,
              borderRadius: 2.5,
              fontWeight: 700,
              fontSize: '0.95rem',
              textTransform: 'none',
              bgcolor: GREEN,
              boxShadow: '0 4px 14px rgba(0,106,99,0.18)',
              '&:hover': { bgcolor: GREEN_DARK, boxShadow: '0 6px 18px rgba(0,106,99,0.24)' },
            }}
          >
            Buy credit pack
          </Button>
          <Typography
            sx={{
              mt: 1, textAlign: 'center', fontSize: '0.7rem', color: GREY_TEXT, fontWeight: 500,
            }}
          >
            Instant balance top-up • Secure checkout
          </Typography>
        </Box>
      )}
    </Box>
  );

  /** One headed half of the tick list. */
  const featureGroup = (heading, rows) => (rows.length > 0 ? (
    <Box>
      <Typography
        component="h4"
        sx={{
          fontSize: '0.68rem',
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: GREY_TEXT,
          pb: 0.9,
          mb: 1.2,
          borderBottom: '1px solid',
          borderColor: '#EFF1ED',
        }}
      >
        {heading}
      </Typography>
      <PlanFeatureList features={rows} iconVariant="badge" />
    </Box>
  ) : null);

  const actions = (
    <>
      {/* Primary: what this card is, or the one action it offers.
          Current and Default are STATES, not actions, so they are disabled —
          with explicit Mui-disabled styling, or MUI greys the green away. */}
      <Button
        fullWidth
        size="large"
        variant="outlined"
        disabled={!isDowngrade || disabled}
        onClick={isDowngrade ? onDowngrade : undefined}
        startIcon={isDowngrade ? <DowngradeIcon /> : <CheckIcon />}
        sx={{
          py: 1.4,
          borderRadius: 2.5,
          fontWeight: 700,
          fontSize: '0.95rem',
          textTransform: 'none',
          boxShadow: 'none',
          // Downgrade is the quietest treatment on the card: a real action,
          // never the one being encouraged. Both branches are PlanCard's, so
          // "Current plan" here and "Current Plan" there are the same button.
          ...(isDowngrade
            ? outlinedIn(GREY_TEXT, GREY_BORDER)
            : {
              ...outlinedIn(GREEN, GREEN, GREEN_TINT),
              '&.Mui-disabled': {
                bgcolor: '#FFFFFF',
                borderColor: GREEN,
                color: GREEN,
                opacity: 0.9,
              },
            }),
        }}
      >
        {/* OLD: {BUTTON_TEXT[action]} */}
        {buttonText(action, startsOn)}
      </Button>

      {/* The pay-as-you-go CTA is no longer here. It belongs under the packs
          it buys, in the middle column, where the choice is made — a button
          three columns away from the thing it acts on is a button you have to
          go looking for. See packsPanel. */}
    </>
  );

  // The two headed halves, side by side when the column is wide enough.
  const featuresPanel = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: 'minmax(0, 1fr)',
          '@container (min-width: 900px)': {
            gridTemplateColumns: 'minmax(0, 0.9fr) minmax(0, 1.1fr)',
          },
        }}
      >
        {featureGroup('Credit usage & rules', usageTicks)}
        {featureGroup('Included capabilities', capabilityTicks)}
      </Box>

      {/* The closing line of the card: what the plan is good at on the left,
          and the one thing it cannot do — more than one website — answered on
          the right, where somebody who has just read "Connect 1 website" is
          looking. Muted, because neither is a claim being sold. */}
      <Box
        sx={{
          mt: 'auto',
          pt: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          flexWrap: 'wrap',
          borderTop: '1px solid',
          borderColor: '#EFF1ED',
          fontSize: '0.7rem',
        }}
      >
        <Typography sx={{ fontSize: '0.7rem', color: GREY_TEXT }}>
          Publishes straight into WordPress
        </Typography>
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: GREEN }}>
          Need more websites? Upgrade anytime
        </Typography>
      </Box>
    </Box>
  );

  // ── The wide row ──────────────────────────────────────────────────────────
  // REQUESTED: below the three plans, spanning the full container, with the
  // details unchanged — and SHORT. The full width only earns its place if the
  // features spread across it; a tall card across the whole row is the failure
  // this layout was reworked to remove. It stacks back into the column order
  // when narrow.
  if (isHorizontal) {
    return (
      <Card
        data-testid="basic-plan-card"
        // The card is its own size container, so the arrangement inside follows
        // the CARD's width rather than the viewport's. See below for why.
        sx={{ ...CARD_SX, containerType: 'inline-size' }}
      >
        {/* ── Three columns, divided, as the reference draws it ──────────────
            3 / 4 / 5 of the row: the plan and its price, the packs and their
            CTA, then everything the plan includes. The proportions are the
            reference's and they earn their places — the middle column is the
            only one with a decision in it, and the right one is the only one
            with nine of anything.

            Arranged by the CARD's width, not the screen's: Dashboard's sidebar
            is a fixed 256px at every width, so the viewport says little about
            how much room this card really has.

              ≥ 1000px  three columns, divided by rules
              < 1000px  stacked in the same order, divided by rules across

            One grid with one column list per size, rather than grid areas:
            the DOM order IS the reading order here — plan, then what it costs
            to top up, then what it does — so nothing needs rearranging for a
            screen reader.

            The dividers are borders on the second and third columns rather
            than a `divide-x` utility: the same line, and it flips from a left
            border to a top border when the columns stack. */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr)',
            '@container (min-width: 1000px)': {
              gridTemplateColumns: 'minmax(0, 3fr) minmax(0, 4fr) minmax(0, 5fr)',
              alignItems: 'stretch',
            },
          }}
        >
          <Box
            sx={{
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              // The identity column holds the plan at the top and its one
              // action at the foot, so the two buttons on the row — Downgrade
              // and Buy — sit on the same line.
              justifyContent: 'space-between',
            }}
          >
            <Box>{identity}</Box>
            <Box sx={{ pt: 3, mt: 3, borderTop: '1px solid', borderColor: '#EFF1ED' }}>
              {actions}
            </Box>
          </Box>

          {/* A tint, so the column carrying the decision reads as a panel
              rather than as more of the card. */}
          <Box
            sx={{
              p: 3,
              minWidth: 0,
              bgcolor: '#FAFBFA',
              borderTop: '1px solid',
              borderColor: '#EFF1ED',
              '@container (min-width: 1000px)': {
                borderTop: 'none',
                borderLeft: '1px solid',
                borderColor: '#EFF1ED',
              },
            }}
          >
            {packsPanel}
          </Box>

          <Box
            sx={{
              p: 3,
              minWidth: 0,
              borderTop: '1px solid',
              borderColor: '#EFF1ED',
              '@container (min-width: 1000px)': {
                borderTop: 'none',
                borderLeft: '1px solid',
                borderColor: '#EFF1ED',
              },
            }}
          >
            {featuresPanel}
          </Box>
        </Box>
      </Card>
    );
  }

  return (
    <Card
      data-testid="basic-plan-card"
      sx={{
        ...CARD_SX,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* The column card is the same four blocks in the same order, stacked —
          the wide row's three columns read top to bottom. Nothing here is a
          second copy of anything: every block is the one built above. */}
      <CardContent sx={{ flexGrow: 1, p: 3 }}>
        {identity}
        {packsPanel && (
          <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: '#EFF1ED' }}>
            {packsPanel}
          </Box>
        )}
        <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: '#EFF1ED' }}>
          {featuresPanel}
        </Box>
      </CardContent>

      <Box sx={{ p: 3, pt: 0 }}>
        {actions}
      </Box>
    </Card>
  );
};

export default BasicPlanCard;
